import { Link } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import SectionHeader from '@/components/SectionHeader';

function AURAMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <rect x="6" y="6" width="24" height="24" rx="6" fill="currentColor" />
      <rect x="14" y="14" width="8" height="8" rx="2" fill="#fbfaf6" />
    </svg>
  );
}

function GlobeIllustration({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 560 560"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Claymation globe with tiffany blue trade routes connecting continents"
    >
      <defs>
        <radialGradient id="globeBg" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#f4f1ea" />
        </radialGradient>
        <filter id="globeShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="24" stdDeviation="28" floodColor="#0abab5" floodOpacity="0.14" />
        </filter>
      </defs>
      <circle cx="280" cy="280" r="260" fill="url(#globeBg)" filter="url(#globeShadow)" />
      <circle cx="280" cy="280" r="260" stroke="rgba(10, 186, 181, 0.08)" strokeWidth="1" />

      {/* Continents */}
      <g fill="#4dd4cf" opacity="0.85">
        <path d="M160 220c18-26 52-34 78-20s42 46 36 74-30 50-58 54-58-12-74-36-10-46 18-72Z" />
        <path d="M340 150c28-8 58 6 72 32s8 58-14 80-54 26-80 14-40-40-34-68 22-50 56-58Z" />
        <path d="M370 330c22-6 46 4 58 26s6 48-12 66-44 22-66 12-36-32-30-58 20-40 50-46Z" />
        <path d="M180 380c14-20 40-26 62-16s34 34 28 56-26 38-50 42-46-8-58-30-8-32 18-52Z" />
      </g>

      {/* Trade routes */}
      <g stroke="#0abab5" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.7">
        <path d="M220 240c40-30 120-20 180 20" />
        <path d="M260 360c30 20 100 10 150-30" />
        <path d="M360 260c-20 40-60 90-110 110" />
        <path d="M200 300c30-10 80-20 130-10" />
      </g>

      {/* Route nodes */}
      <g fill="#3cbfba">
        <circle cx="220" cy="240" r="7" />
        <circle cx="400" cy="260" r="7" />
        <circle cx="360" cy="360" r="7" />
        <circle cx="260" cy="360" r="7" />
        <circle cx="200" cy="300" r="7" />
      </g>

      {/* Latitude/longitude lines */}
      <g stroke="rgba(10, 186, 181, 0.06)" strokeWidth="1" fill="none">
        <ellipse cx="280" cy="280" rx="260" ry="100" />
        <ellipse cx="280" cy="280" rx="260" ry="180" />
        <ellipse cx="280" cy="280" rx="100" ry="260" />
        <ellipse cx="280" cy="280" rx="180" ry="260" />
      </g>
    </svg>
  );
}

export default function DistributorSection() {
  return (
    <section className="border-t border-rp-line bg-rp-paper py-16 md:py-24">
      <div className="rp-container">
        <div className="grid items-center gap-9 md:grid-cols-2 md:gap-16">
          {/* Copy */}
          <div className="order-2 text-center md:order-none md:text-left">
            <SectionHeader
              eyebrow="Manufacturer · Distributor"
              title={
                <>
                  Made and tested by{' '}
                  <em className="not-italic text-rp-forest">AURA</em>
                </>
              }
              description="Every vial is lyophilized, sealed and independently tested by AURA. We publish COA and HPLC certificates before each lot ships, and dispatch from the UAE with full documentation."
              align="left"
            />

            <div className="mt-8 inline-flex items-center gap-3 rounded-lg border border-rp-line bg-rp-bone px-6 py-4">
              <AURAMark className="h-8 w-8 text-rp-forest" />
              <span className="text-[22px] font-bold leading-none tracking-tight text-rp-forest">
                AURA
              </span>
            </div>

            <div className="mt-7 flex flex-col items-stretch gap-3 md:mt-8 md:items-start">
              <Link to="/about" className="rp-btn-primary justify-center md:justify-start">
                How we work together
                <ArrowRight size={14} />
              </Link>
              <Link
                to="/about"
                className="rp-btn-ghost justify-center md:justify-start"
              >
                About AURA
              </Link>
            </div>
          </div>

          {/* Visual */}
          <div className="order-1 flex flex-col items-center justify-center gap-3 md:order-none md:gap-6">
            <div className="relative flex w-full items-center justify-center">
              <div
                aria-hidden="true"
                className="absolute -z-10"
                style={{
                  inset: '-8%',
                  background: 'radial-gradient(closest-side, rgba(77, 212, 207, 0.34), rgba(77, 212, 207, 0) 70%)',
                  filter: 'blur(14px)',
                }}
              />
              <div className="relative w-[80vw] max-w-[320px] md:w-full md:max-w-[560px]" style={{ aspectRatio: '1 / 1' }}>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="How every AURA vial is made and tested"
                  className="group relative h-full w-full cursor-pointer overflow-hidden rounded-3xl bg-rp-bone transition-transform duration-200 hover:scale-[1.02]"
                  onClick={() => window.open('https://peptide-aura.store/', '_blank')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      window.open('https://peptide-aura.store/', '_blank');
                    }
                  }}
                >
                  <GlobeIllustration className="h-full w-full object-cover" />
                  <span
                    aria-hidden="true"
                    className="absolute right-4 top-4 inline-flex items-center gap-2 bg-rp-forest/60 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-rp-paper backdrop-blur-sm"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-rp-leaf-2 shadow-[0_0_0_3px_rgba(10,186,181,0.25)]" />
                    Muted
                  </span>
                  <span className="absolute bottom-4 left-4 inline-flex items-center gap-2.5 rounded-full border border-rp-line bg-rp-paper px-4 py-2.5 text-sm font-semibold tracking-tight text-rp-forest shadow-card-hover">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-rp-forest text-rp-paper">
                      <Play size={12} fill="currentColor" />
                    </span>
                    Watch the film · 2:11
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-rp-moss">
              <span aria-hidden="true" className="h-px w-7 bg-rp-line" />
              <span className="h-2 w-2 rounded-full bg-rp-leaf shadow-[0_0_0_4px_rgba(10,186,181,0.18)]" />
              WORLDWIDE · UAE ORIGIN
              <span aria-hidden="true" className="h-px w-7 bg-rp-line" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
